<script setup>
import {
  computed,
  ref,
  watch,
  nextTick,
} from 'vue';

import { formatDate } from 'date-fns';

import Chart from 'primevue/chart';

import { storeToRefs } from 'pinia';

import Card from 'primevue/card';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import SelectButton from 'primevue/selectbutton';

import useTrackingLinksStore from '@/stores/tracking-links';

import { barChartOptions } from '@/composables/useChartConfigs';

const props = defineProps({
  startDate: {
    type: Date,
    required: true,
  },

  endDate: {
    type: Date,
    required: true,
  },
});

const {
  isFetching,
  analytics,
} = storeToRefs(useTrackingLinksStore());

// Delay chart rendering to ensure DOM is ready after transition
const chartReady = ref(false);
let chartTimer;
watch(isFetching, async (fetching) => {
  clearTimeout(chartTimer);
  if (!fetching) {
    chartReady.value = false;
    await nextTick();
    chartTimer = setTimeout(() => {
      chartReady.value = true;
    }, 100);
  } else {
    chartReady.value = false;
  }
}, { immediate: true });

const view = ref('chart');

const viewOptions = ref([{
  icon: 'far fa-chart-line',
  value: 'chart',
  tooltip: 'Chart View',
}, {
  icon: 'far fa-table-layout',
  value: 'table',
  tooltip: 'Table View',
}]);

const chartInterval = computed(() => {
  // If range is set to "All Time", we'll calculate interval based on the
  // oldest record in the dataset.
  const overTime = analytics.value?.overTime || [];
  const start = props.startDate || new Date(overTime?.[0]?.key || 0);

  const daysDifference = Math.ceil((props.endDate - start) / (1000 * 60 * 60 * 24));
  return daysDifference <= 1 ? 'hours' : 'days';
});

/**
 * Get range of dates used to generate labels and datasets,
 * based on the startDate and endDate range.
 * If the range is <= 1 day, then we'll split the day by hour instead
 * of day.
 * @returns {Array<Date>} List of dates to use for X-Axis ticks.
 */
const getTimeTicks = () => {
  const timeTicks = [];

  const overTime = analytics.value?.overTime || [];
  const start = props.startDate || new Date(overTime?.[0]?.key || 0);

  if (chartInterval.value === 'days') {
    // If more than one day, use dates as ticks.
    const currentDate = new Date(start);

    // Reset to start of day to make comparing easier.
    currentDate.setHours(0, 0, 0, 0);

    while (currentDate <= props.endDate) {
      timeTicks.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
  } else {
    // If one day or less, use hours as ticks.
    const currentDate = new Date(start);

    // We don't care about minutes or seconds,
    // and don't want them showing on the chart.
    currentDate.setMinutes(0, 0, 0);

    while (currentDate <= props.endDate) {
      timeTicks.push(new Date(currentDate));
      currentDate.setHours(currentDate.getHours() + 1);
    }
  }

  return timeTicks;
};

/**
 * Format chart labels using ticks generated by start
 * and end date range.
 * If range is <= 1 day, then labels will be hours instead of
 * dates.
 * @returns {Array<string>} List of labels to use for X-Axis of Chart.
 */
const getChartLabels = () => {
  const ticks = getTimeTicks();
  return ticks.map((tick) => {
    // Format the date to a string that can be used as a label.
    // If the range is <= 1 day, format as 'hh:mm', otherwise 'yyyy-MM-dd'.
    return formatDate(tick, chartInterval.value === 'days' ? 'MM/dd' : 'MM/dd ha');
  });
};

/**
 * Get actual chart data per tick in the graph.
 * By default, it'll return the overall visit count (doc_count),
 * but if passed "unique_count" it will give unique visits instead.
 * @param {string} property - The property to return from the analytics data.
 * @returns {Array<number>} List of data points to use for Y-Axis of Chart.
 */
const getChartData = (property = 'doc_count') => {
  const overTime = analytics.value?.overTime || [];
  const ticks = getTimeTicks();

  return ticks.map((d) => {
    return overTime.find((data) => {
      // Check if data.key (unix timestamp; UTC) matches the date tick,
      // returning true when found.
      return new Date(data.key).toISOString() === d.toISOString();
    })?.[property] || 0;
  });
};

const chartData = computed(() => {
  return {
    labels: getChartLabels(),
    datasets: [
      {
        label: 'Total',
        data: getChartData('doc_count'),
        fill: false,
        borderColor: '#00B4CC',
        type: 'line',
        borderWidth: 2,
        tension: 0.4,
        pointHoverRadius: 15,
        datalabels: {
          anchor: 'center', // Position in the middle of the point
        },
      },
      {
        label: 'Unique',
        data: getChartData('unique_count'),
        fill: false,
        borderColor: '#B90071',
        type: 'line',
        borderWidth: 2,
        tension: 0.4,
        pointHoverRadius: 15,
        datalabels: {
          anchor: 'center', // Position in the middle of the point
        },
      },
    ],
  };
});

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,

  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        stepSize: 1,
      },
    },
  },

  plugins: {
    legend: {
      display: true,
    },
    datalabels: barChartOptions.value.plugins.datalabels,
    tooltip: barChartOptions.value.plugins.tooltip,
  },
}));

const tableData = computed(() => {
  const overTime = analytics.value?.overTime || [];

  // Use same time ticks as chart, but filter out days without data.
  const ticks = getTimeTicks().filter((d) => (
    overTime.some((data) => new Date(data.key).toISOString() === d.toISOString())
  ));

  return ticks.map((d) => {
    const data = overTime.find((rec) => new Date(rec.key).toISOString() === d.toISOString());

    return {
      date: formatDate(d, chartInterval.value === 'days' ? 'MMM dd, yyyy' : 'MMM dd, ha'),
      totalVisits: data?.doc_count || 0,
      uniqueVisits: data?.unique_count || 0,
    };
  });
});
</script>

<template>
  <!-- Visits Over Time -->
  <Card
    pt:root:class="analytics-card analytics-card__total-visits-map shadow-2 mb-3 relative"
    pt:body:class="w-full"
  >
    <template #content>
      <div v-if="isFetching" class="flex flex-column justify-content-center align-items-center">
        <Skeleton height="1rem" width="6rem" class="mb-2" />
        <Skeleton height="4rem" width="4rem" />
      </div>

      <Transition name="fade-in-out" mode="out-in">
        <div v-if="!isFetching" :key="view">
          <SelectButton
            class="analytics-card__view-type-toggle"
            v-model="view"
            :options="viewOptions"
            option-label="value"
            option-value="value"
            aria-labelledby="custom"
            data-entity="analytics-trackingLink-overTimeViewSwitcher"
            data-test="analytics-trackingLink-overTimeViewSwitcher"
          >
            <template #option="slotProps">
              <font-awesome-icon
                :icon="slotProps.option.icon"
                v-tooltip="slotProps.option.tooltip"
              />
            </template>
          </SelectButton>

          <h5 class="text-center p-0 m-0 mb-2">Visits Over Time</h5>

          <Chart
            v-if="chartReady && view === 'chart'"
            type="line"
            :data="chartData"
            :options="chartOptions"
            :height="300"
          />

          <DataTable
            v-else-if="view === 'table'"
            :value="tableData"
            pt:wrapper:class="shadow-none"
            aria-label="Visits over Time"
            scrollHeight="300px"
            data-test="analytics-trackingLink-visitsOverTime"
            data-entity="analytics-trackingLink-visitsOverTime"
          >
            <Column field="date" header="Date" />

            <Column
              field="totalVisits"
              header="Total Visits"
              class="analytics-card__table-column-small"
            />

            <Column
              field="uniqueVisits"
              header="Unique Visits"
              class="analytics-card__table-column-small"
            />
          </DataTable>
        </div>
      </Transition>
    </template>
  </Card>
  <!-- End Visits Over Time -->
</template>

<style>
.analytics-card__view-type-toggle {
  position: absolute;
  right: 0.5rem;
  top: 0.5rem;
  z-index: 1;
}

.p-selectbutton .p-button {
  min-width: unset;
}
</style>
